import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import resizeImage from 'utils/resizeImage';
import { updateAuthUser } from '../../api/api';
import ProfileDetailsForm from '../../components/Forms/ProfileDetailsForm/ProfileDetailsForm';
import Icon from '../../components/Icon/Icon';
import Image from '../../components/Image/Image';
import PhotoCropper from '../../components/Modal/PhotoCropper/PhotoCropper';
import Tab, { TabContent } from '../../components/Tab/Tab';
import { showAlert, useAppContext } from '../../context/appContext';
import { AuthUser, HTTPError } from '../../types/types';
import readAWSS3File from '../../utils/readAWSS3File';
import uploadAWSS3File from '../../utils/uploadAWSS3File';
import { AUTH_USER_STORE_KEY, formatPhoneNumber, storage, US_STATES } from '../../utils/utils';
import getStateFromZipCode from '../../utils/getStateFromZipCode';

function Profile() {
  const [{ user }, dispatch] = useAppContext();
  const [showPhotoCropperModal, setShowPhotoCropperModal] = React.useState(false);
  const [uploadedPhotoFile, setUploadedPhotoFile] = React.useState<File>();
  const [uploadingPhoto, setUploadingFile] = React.useState(false);
  const [croppedPhoto, setCroppedPhoto] = React.useState<Blob>();

  const { mutate: updateProfileFn, isLoading } = useMutation(
    (variables: { payload: Record<string, any> }) => updateAuthUser(variables.payload),
    {
      onSettled: (_, error, variables) => {
        if (error) {
          showAlert(dispatch, {
            show: true,
            message: (error as HTTPError).message,
            type: 'ERROR',
          });
          return;
        }

        if (user) {
          const updatedProfile = {
            ...user,
            ...variables.payload,
            patientDetails: {
              ...user.patientDetails,
              gender: variables.payload?.patientDetails?.gender,
            },
          };

          dispatch({ type: 'SET_AUTH_USER', payload: updatedProfile });

          storage.set(AUTH_USER_STORE_KEY, JSON.stringify(updatedProfile));
        }

        showAlert(
          dispatch,
          {
            show: true,
            message: 'Profile details updated 🎉!',
            type: 'SUCCESS',
          },
          4000,
        );
      },
    },
  );

  async function handleUploadedPhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const { files } = event.target;
    if (files && files.length > 0) {
      const resizedFile = await resizeImage(files[0], 0.5);
      setUploadedPhotoFile(resizedFile);
      setShowPhotoCropperModal(true);
    }
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  }

  const handlePhotoUpdate = async (blob: Blob) => {
    setCroppedPhoto(blob);

    const filename = uploadedPhotoFile?.name;

    const fileType = uploadedPhotoFile?.type || 'image/jpeg';

    const fileKey = `avatar/${uuidv4()}.${filename ? filename.split('.').pop() : 'jpeg'}`;

    setUploadingFile(true);

    const uploadedPhoto = await uploadAWSS3File(blob, fileKey, fileType, 'public');

    if (!uploadedPhoto) {
      setCroppedPhoto(undefined);

      setUploadingFile(false);

      showAlert(dispatch, {
        show: true,
        message: 'Oops. Issue updating your account photo. Please try again!',
        type: 'ERROR',
      });

      return;
    }

    await updateAuthUser({ profilePicture: uploadedPhoto });

    const photo = await readAWSS3File('avatar', uploadedPhoto);

    const mergedAuthUser = {
      ...user,
      signedAvatar: photo,
      profilePicture: uploadedPhoto,
    } as AuthUser;

    setUploadingFile(false);

    dispatch({ type: 'SET_AUTH_USER', payload: mergedAuthUser });

    storage.set(AUTH_USER_STORE_KEY, JSON.stringify(mergedAuthUser));
  };

  const handleDetailsUpdate = (payload: Partial<AuthUser>) => {
    if (payload.zipCode && payload.state !== getStateFromZipCode(payload.zipCode).code) {
      const stateName = US_STATES.find(fndState => fndState.value === payload.state);

      showAlert(
        dispatch,
        {
          show: true,
          message: `Sorry! Your selected state ${
            stateName ? `"${stateName.label}"` : ''
          } does not correspond with the zip code "${payload.zipCode}". Please try again!`,
          type: 'ERROR',
        },
        6000,
      );
      return;
    }

    updateProfileFn({ payload });
  };

  return (
    <>
      <div className="-mt-8">
        <div
          className="bg-profile-cover-header-mobile-graphic tablet:bg-profile-cover-header-graphic 
          h-[250px] tablet:h-[350px] bg-cover bg-center bg-[#ffe0af] -mx-6 tablet:-mx-10 p-0 border-0"
        />
        <div className="flex flex-col tablet:flex-row space-y-12 tablet:space-y-0 tablet:gap-10 -mt-20 tablet:-mt-14">
          <div
            className="shrink-0  bg-white w-fit  border-[1px] border-white rounded-full  mx-auto
          tablet:w-[40%] laptop:w-[25%] tablet:mx-0 tablet:rounded-xl tablet:shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] tablet:pt-14"
          >
            <div className="relative w-fit mx-auto">
              {uploadingPhoto && (
                <Icon
                  id="loader"
                  className="w-10 inline-flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                />
              )}
              <Image
                className="w-[150px] h-[150px] tablet:h-40 tablet:w-40 rounded-full"
                src={
                  (croppedPhoto && URL.createObjectURL(croppedPhoto)) ||
                  (user && user.signedAvatar) ||
                  'image_doc_avatar_placeholder.png'
                }
                alt="user avatar"
              />
              <button
                type="button"
                className="bottom-[10px] right-0 tablet:bottom-2 tablet:right-1 cursor-pointer absolute 
                bg-river-blue h-9 w-9 rounded-full border-[3px] border-white flex justify-center items-center"
              >
                <Icon id="iconProfileCameraUpload" className="w-5 h-5" />
                <input
                  type="file"
                  accept="image/*"
                  className="absolute rounded-full w-10 h-10 opacity-0 file:cursor-pointer"
                  onChange={handleUploadedPhoto}
                />
              </button>
            </div>
            <p className="hidden tablet:block font-poppins-regular text-sm text-center mt-6">
              {user ? `${user.firstName} ${user.lastName}` : ''}
            </p>
            <div className="hidden tablet:block mt-12">
              <div className="border-t-[1px] border-[#e9ecef] py-4 px-6">
                <span className="text-xs text-gray-400">Email</span>
                <p className="text-sm break-words">{user?.email}</p>
              </div>
              <div className="border-t-[1px] border-[#e9ecef] py-4 px-6">
                <span className="text-xs text-gray-400">Phone</span>
                <p className="text-sm">{user ? formatPhoneNumber(user.phoneNumber) : ''}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 tablet:rounded-xl tablet:shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] tablet:bg-white">
            <Tab defaultActiveKey="account" id="profileTab">
              <TabContent eventKey="account" title="Account Info">
                {user && (
                  <ProfileDetailsForm
                    account={{
                      firstName: user.firstName,
                      lastName: user.lastName,
                      email: user.email,
                      gender: user.patientDetails?.gender || 'MALE',
                      phoneNumber: user.phoneNumber,
                      zipCode: user.zipCode || '',
                      state: user.state || '',
                    }}
                    isUpdating={isLoading}
                    onUpdateAccount={handleDetailsUpdate}
                  />
                )}
              </TabContent>
            </Tab>
          </div>
        </div>
      </div>

      {showPhotoCropperModal && uploadedPhotoFile && (
        <PhotoCropper
          photo={uploadedPhotoFile}
          onCloseModal={() => setShowPhotoCropperModal(false)}
          onSetCroppedPhoto={handlePhotoUpdate}
        />
      )}
    </>
  );
}

export default Profile;
